import type { Client } from '@urql/vue';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';

import type { BestPrice } from '@/composables/bestPrice';
import { readPriceUnit } from '@/composables/preferredOrderUnit';
import type {
  GetMultipleBestPricesQuery,
  GetMultipleBestPricesQueryVariables,
} from '@/generated/graphql';
import type { FischerPlugin } from '@/lib/fischerPlugin';

const GET_MULTIPLE_BEST_PRICES = () => import('@/graphql/getMultipleBestPrices.gql');

const BATCH_SIZE = 20;

type PriceItem = {
  productCode: string;
  qty?: number;
  getRootCategory?: boolean;
  /**
   * Asks for the price in this unit rather than the session's. A cart position
   * ordered in packages or pallets needs its scale prices quoted in that unit,
   * or its quantity is compared against thresholds in another one.
   */
  unit?: string;
};

const cacheKey = (
  productCode: string,
  qty?: number,
  getRootCategory?: boolean,
  unit?: string,
): string => {
  const base = qty == null ? productCode : `${productCode}@${qty}`;
  const withRootCategory = getRootCategory ? `${base}#rc` : base;
  return unit ? `${withRootCategory}!${unit}` : withRootCategory;
};

export const useBestPriceStore = defineStore('useBestPriceStore', () => {
  // shallowRef prevents deep reactive unwrapping of FischerPlugin's internal Refs
  const client = shallowRef<Client | null>(null);
  const fischer = shallowRef<FischerPlugin | null>(null);

  // Records-in-refs are reactive in Vue 3 (the ref wraps a reactive proxy),
  // so adding/deleting keys triggers the getters below.
  const cache = ref<Record<string, BestPrice | null>>({});
  const inFlight = ref<Record<string, true>>({});
  const errored = ref<Record<string, true>>({});

  let lastSignature: string | null = null;

  /** Session-level, not per-item: it comes from the customer profile. */
  const currentUnit = (): string | undefined => readPriceUnit(fischer.value);

  const contextSignature = (): string => {
    const $fischer = fischer.value;
    if (!$fischer) {
      return '';
    }
    return [
      $fischer.user.id(),
      $fischer.user.division(),
      $fischer.user.distributionChannel(),
      $fischer.siteLanguage(),
      currentUnit() ?? '',
    ].join('|');
  };

  const clear = () => {
    cache.value = {};
    inFlight.value = {};
    errored.value = {};
  };

  // `unit` is the one the price was asked for in; omitted, it is the session's.
  const priceFor = (
    productCode: string,
    qty?: number,
    getRootCategory?: boolean,
    unit?: string,
  ): BestPrice | null | undefined =>
    cache.value[cacheKey(productCode, qty, getRootCategory, unit ?? currentUnit())];

  const isLoading = (
    productCode: string,
    qty?: number,
    getRootCategory?: boolean,
    unit?: string,
  ): boolean =>
    Boolean(inFlight.value[cacheKey(productCode, qty, getRootCategory, unit ?? currentUnit())]);

  const hasError = (
    productCode: string,
    qty?: number,
    getRootCategory?: boolean,
    unit?: string,
  ): boolean =>
    Boolean(errored.value[cacheKey(productCode, qty, getRootCategory, unit ?? currentUnit())]);

  const fetchPrices = async (
    urqlClient: Client,
    $fischer: FischerPlugin,
    items: PriceItem[],
    getRootCategory: boolean,
    unit: string | undefined,
  ): Promise<{ prices: Record<string, BestPrice>; error: unknown }> => {
    const query = (await GET_MULTIPLE_BEST_PRICES()).default;
    // urql resolves with { data, error } and never throws; partial data may
    // accompany an error (equivalent to Apollo's errorPolicy: 'all').
    const { data, error } = await urqlClient
      .query<GetMultipleBestPricesQuery, GetMultipleBestPricesQueryVariables>(
        query,
        {
          baseSiteId: $fischer.baseSiteId(),
          language: $fischer.siteLanguage(),
          userId: $fischer.user.id(),
          accessToken: $fischer.user.accessToken(),
          division: $fischer.user.division(),
          distributionChannel: $fischer.user.distributionChannel(),
          catalogLocale: $fischer.catalogLocale(),
          getRootCategory,
          // Scale prices ride along with every best price, the way
          // getBestPrice.gql already fetches them for a single product. They
          // are a handful of rows and empty for most articles, so they do not
          // warrant their own cache key.
          includeBestPriceSavings: true,
          queries: items.map((item) => {
            const itemUnit = item.unit ?? unit;
            return {
              productCode: item.productCode,
              ...(item.qty == null ? {} : { qty: item.qty }),
              ...(itemUnit ? { unit: itemUnit } : {}),
            };
          }),
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    const prices: Record<string, BestPrice> = {};
    (data?.getMultipleBestPrices ?? []).forEach((bestPrice) => {
      const code = bestPrice?.productCode;
      if (code && bestPrice) {
        prices[code] = bestPrice;
      }
    });

    return { prices, error };
  };

  const fetchChunk = async (
    urqlClient: Client,
    $fischer: FischerPlugin,
    items: PriceItem[],
    unit: string | undefined,
  ) => {
    const getRootCategory = items[0]?.getRootCategory ?? false;
    const keyByCode: Record<string, string> = {};
    items.forEach((item) => {
      const key = cacheKey(item.productCode, item.qty, item.getRootCategory, item.unit ?? unit);
      keyByCode[item.productCode] = key;
      inFlight.value[key] = true;
      delete errored.value[key];
    });
    try {
      const { prices, error } = await fetchPrices(
        urqlClient,
        $fischer,
        items,
        getRootCategory,
        unit,
      );

      // Always process whatever data was returned (an error may accompany data)
      Object.entries(prices).forEach(([code, bestPrice]) => {
        if (keyByCode[code]) {
          cache.value[keyByCode[code]] = bestPrice;
        }
      });
      if (error) {
        console.error('Error fetching best prices', error);
      }
      // For each requested item still absent from cache after processing data:
      // - if there was an error, mark as retryable (errored)
      // - else write null (confirmed no price)
      items.forEach((item) => {
        const key = cacheKey(item.productCode, item.qty, item.getRootCategory, item.unit ?? unit);
        if (cache.value[key] === undefined) {
          if (error) {
            errored.value[key] = true;
          } else {
            cache.value[key] = null;
          }
        }
      });
    } catch (error) {
      console.error('Error fetching best prices', error);
      items.forEach((item) => {
        errored.value[
          cacheKey(item.productCode, item.qty, item.getRootCategory, item.unit ?? unit)
        ] = true;
      });
    } finally {
      items.forEach((item) => {
        delete inFlight.value[
          cacheKey(item.productCode, item.qty, item.getRootCategory, item.unit ?? unit)
        ];
      });
    }
  };

  const ensure = async (items: PriceItem[]) => {
    if (IS_SSR) {
      return;
    }
    const urqlClient = client.value;
    const $fischer = fischer.value;
    if (!urqlClient || !$fischer) {
      console.error('Best price store not initialized');
      return;
    }

    // lazy invalidation: drop the cache when the price-affecting context changes
    const signature = contextSignature();
    if (lastSignature !== null && signature !== lastSignature) {
      clear();
    }
    lastSignature = signature;

    const unit = currentUnit();

    // drop cached, in-flight, and duplicate keys
    const seen = new Set<string>();
    const todo = items.filter((item) => {
      const key = cacheKey(item.productCode, item.qty, item.getRootCategory, item.unit ?? unit);
      if (seen.has(key) || cache.value[key] !== undefined || inFlight.value[key]) {
        return false;
      }
      seen.add(key);
      return true;
    });
    if (!todo.length) {
      return;
    }

    // chunk into <= BATCH_SIZE distinct-code requests; a chunk is homogeneous in
    // getRootCategory because that is a single query-level variable
    const chunks: PriceItem[][] = [];
    for (const item of todo) {
      let placed = false;
      for (const chunk of chunks) {
        if (
          chunk.length < BATCH_SIZE &&
          (chunk[0].getRootCategory ?? false) === (item.getRootCategory ?? false) &&
          !chunk.some((c) => c.productCode === item.productCode)
        ) {
          chunk.push(item);
          placed = true;
          break;
        }
      }
      if (!placed) {
        chunks.push([item]);
      }
    }
    await Promise.all(chunks.map((chunk) => fetchChunk(urqlClient, $fischer, chunk, unit)));
  };

  const ensurePrices = (codes: string[], getRootCategory?: boolean) =>
    ensure(codes.map((productCode) => ({ productCode, getRootCategory })));

  const ensurePricesForQuantities = (
    items: { productCode: string; qty: number; unit?: string }[],
    getRootCategory?: boolean,
  ) =>
    ensure(
      items.map((item) => ({
        productCode: item.productCode,
        qty: item.qty,
        unit: item.unit,
        getRootCategory,
      })),
    );

  const initialize = (data: { apollo: Client | undefined; fischer: FischerPlugin }) => {
    if (client.value) {
      return;
    }
    client.value = data.apollo ?? null;
    fischer.value = data.fischer;
    lastSignature = contextSignature();
  };

  return {
    initialize,
    ensurePrices,
    ensurePricesForQuantities,
    priceFor,
    isLoading,
    hasError,
    clear,
  };
});
